import React from 'react';
import { Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { ThicknessAnalysisProfile } from '../../../types/cad';
import { PRESET_NONE } from './helpers';
import { ThicknessPanelController } from './useCADThicknessPanel';

interface ThicknessConditionsFormProps {
  ctrl: ThicknessPanelController;
  uid: string;
  canAnalyze: boolean;
}

export const ThicknessConditionsForm: React.FC<ThicknessConditionsFormProps> = ({
  ctrl,
  uid,
  canAnalyze,
}) => {
  const {
    t,
    presets,
    selectedPresetId,
    selectPreset,
    presetName,
    setPresetName,
    savingPreset,
    savePreset,
    lowerMm,
    upperMm,
    toleranceMm,
    profile,
    setLowerMm,
    setUpperMm,
    setToleranceMm,
    setProfile,
    starting,
    startAnalysis,
  } = ctrl;

  return (
    <>
      <p className="text-sm font-bold text-foreground">
        {t('cad.thickness.analysisConditions')}
      </p>
      <div className="mt-2 flex flex-row items-end gap-1.5">
        <div className="flex min-w-[150px] flex-col gap-1.5">
          <Label htmlFor={`${uid}-preset`} className="text-xs">
            {t('cad.thickness.preset.label')}
          </Label>
          <Select
            value={selectedPresetId || PRESET_NONE}
            onValueChange={(raw) => selectPreset(raw === PRESET_NONE ? '' : raw)}
          >
            <SelectTrigger id={`${uid}-preset`}>
              <SelectValue placeholder={t('cad.thickness.preset.label')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={PRESET_NONE}>{t('cad.thickness.preset.none')}</SelectItem>
              {presets.map((preset) => (
                <SelectItem key={preset.presetId} value={preset.presetId}>
                  {preset.name} v{preset.version}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor={`${uid}-preset-name`} className="text-xs">
            {t('cad.thickness.preset.name')}
          </Label>
          <Input
            id={`${uid}-preset-name`}
            value={presetName}
            onChange={(event) => setPresetName(event.target.value)}
            disabled={!canAnalyze || savingPreset}
          />
        </div>
        <Button
          variant="outline"
          onClick={savePreset}
          disabled={!canAnalyze || savingPreset}
        >
          {t('cad.thickness.preset.save')}
        </Button>
      </div>
      <div className="mt-2 flex flex-row items-end gap-1.5">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor={`${uid}-lower`} className="text-xs">
            {t('cad.thickness.lower')}
          </Label>
          <Input
            id={`${uid}-lower`}
            type="number"
            value={lowerMm}
            min={0}
            step={0.01}
            onChange={(event) => setLowerMm(Number(event.target.value))}
            disabled={!canAnalyze || starting}
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor={`${uid}-upper`} className="text-xs">
            {t('cad.thickness.upper')}
          </Label>
          <Input
            id={`${uid}-upper`}
            type="number"
            value={upperMm}
            min={0}
            step={0.01}
            onChange={(event) => setUpperMm(Number(event.target.value))}
            disabled={!canAnalyze || starting}
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor={`${uid}-tolerance`} className="text-xs">
            {t('cad.thickness.tolerance')}
          </Label>
          <Input
            id={`${uid}-tolerance`}
            type="number"
            value={toleranceMm}
            min={0}
            step={0.01}
            onChange={(event) => setToleranceMm(Number(event.target.value))}
            disabled={!canAnalyze || starting}
          />
        </div>
      </div>
      <div className="mt-2 flex flex-row items-end gap-2">
        <div className="flex min-w-[120px] flex-col gap-1.5">
          <Label htmlFor={`${uid}-profile`} className="text-xs">
            {t('cad.thickness.profile')}
          </Label>
          <Select
            value={profile}
            onValueChange={(value) => setProfile(value as ThicknessAnalysisProfile)}
            disabled={!canAnalyze || starting}
          >
            <SelectTrigger id={`${uid}-profile`}>
              <SelectValue placeholder={t('cad.thickness.profile')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">{t('cad.thickness.profileValues.standard')}</SelectItem>
              <SelectItem value="medium">{t('cad.thickness.profileValues.medium')}</SelectItem>
              <SelectItem value="coarse">{t('cad.thickness.profileValues.coarse')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={() => startAnalysis(null)}
          disabled={!canAnalyze || starting}
          className="flex-1"
        >
          {starting ? <Spinner size="sm" label="" /> : <Play />}
          {t('cad.thickness.start')}
        </Button>
      </div>
      <p className="mt-1.5 block text-xs text-muted-foreground">
        {t('cad.thickness.toleranceHelp', {
          lower: (lowerMm - toleranceMm).toFixed(2),
          upper: (upperMm + toleranceMm).toFixed(2),
        })}
      </p>
    </>
  );
};
